/**
 * "Internet Direto" na criação de VM: de onde vem o endereço público (issue #1061).
 *
 * Dois modos, com os nomes do `wan_mode` do backend (os mesmos do Cloud Gateway):
 *
 * - `pool` — IPv4 fixo reservado do pool: o de sempre, cobrado à parte e reservado ao
 *   cliente mesmo depois de a VM ser excluída;
 * - `dhcp` — lease da rede WAN do provedor: sem custo adicional, mas o endereço não é
 *   reservado, chega depois do boot (quem o conhece é o guest agent) e pode mudar.
 *
 * Funções puras porque a ORDEM importa e é o que regride fácil: a opção de DHCP só
 * existe quando o backend (`resolve-cluster`) disse `wan_dhcp_available === true` — falso
 * OU ausente (resposta antiga, ainda carregando) é "não oferece", em vez de oferecer e
 * cair no 400. Falha fechada: a opção que está em produção é a do pool.
 */

export type WanMode = 'pool' | 'dhcp'

export const WAN_MODE_POOL: WanMode = 'pool'
export const WAN_MODE_DHCP: WanMode = 'dhcp'

/** Quais modos a tela oferece, dado o que o backend respondeu sobre o cluster de destino. */
export function opcoesDeEnderecoPublico(wanDhcpAvailable: boolean | undefined): WanMode[] {
  return wanDhcpAvailable === true ? [WAN_MODE_POOL, WAN_MODE_DHCP] : [WAN_MODE_POOL]
}

/**
 * O modo que a tela deve manter quando a resposta do cluster muda (outro template, outro
 * produto): `dhcp` só sobrevive se o cluster novo o oferece.
 */
export function modoValidoPara(modo: WanMode, wanDhcpAvailable: boolean | undefined): WanMode {
  return opcoesDeEnderecoPublico(wanDhcpAvailable).includes(modo) ? modo : WAN_MODE_POOL
}

export interface EstadoDaRedePublica {
  networkMode: 'vpc' | 'public_bridge'
  wanMode: WanMode
  selectedPublicIpId: string | null
  wanDhcpAvailable: boolean | undefined
}

/**
 * O que trava o botão "Criar VM" por causa da rede pública. `null` = nada trava.
 *
 * - pool exige um IP escolhido (a regra de sempre);
 * - dhcp exige que o backend tenha CONFIRMADO o DHCP — a escolha não pode sobreviver a uma
 *   resposta que a tirou do ar.
 */
export function motivoParaNaoCriar(estado: EstadoDaRedePublica): string | null {
  if (estado.networkMode !== 'public_bridge') return null
  if (estado.wanMode === WAN_MODE_DHCP) {
    return estado.wanDhcpAvailable === true
      ? null
      : 'A rede deste cluster não fornece DHCP: escolha um IPv4 fixo.'
  }
  return estado.selectedPublicIpId ? null : 'Selecione um IPv4 para a VM.'
}

/** O que vai no payload de `POST /instances` para o modo escolhido. */
export function camposDeRedePublica(estado: EstadoDaRedePublica): {
  wan_mode?: WanMode
  public_ip_address_id?: string
} {
  if (estado.networkMode !== 'public_bridge') return {}
  if (estado.wanMode === WAN_MODE_DHCP) return { wan_mode: WAN_MODE_DHCP }
  return {
    wan_mode: WAN_MODE_POOL,
    public_ip_address_id: estado.selectedPublicIpId ?? undefined,
  }
}

export interface DescricaoDeCobranca {
  titulo: string
  cobranca: string
  detalhe: string
}

/**
 * A diferença de preço ENTRE as duas opções, escrita antes da escolha — é com ela à vista
 * que o cliente decide. `precoDoIpv4` é o preço formatado do produto de IPv4 do reseller
 * (ex.: "R$ 49,90/mês") ou `null` quando o catálogo não o tem.
 */
export function descricaoDeCobranca(
  modo: WanMode,
  precoDoIpv4: string | null,
): DescricaoDeCobranca {
  if (modo === WAN_MODE_DHCP) {
    return {
      titulo: 'Endereço por DHCP da rede do provedor',
      cobranca: 'Sem custo adicional',
      detalhe:
        'O endereço não é reservado: é entregue pela rede no boot, aparece aqui pelo guest ' +
        'agent e pode mudar a cada renovação do lease. Não sirva por ele nada que dependa de ' +
        'um IP fixo (DNS, regras que apontam para um endereço).',
    }
  }
  return {
    titulo: 'IPv4 fixo do pool',
    cobranca: precoDoIpv4 ? `${precoDoIpv4}, cobrado à parte da VM` : 'Cobrado à parte da VM',
    detalhe:
      'Endereço dedicado e reservado para você: continua seu (e cobrado) mesmo depois de ' +
      'excluir a VM, até você liberá-lo em Rede → IPs Públicos.',
  }
}
